<!-- 客户面谈的添加 / 编辑表单弹窗 -->
<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="55%">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="110px"
    >
      <el-row>
        <el-col :span="12">
          <el-form-item label="面谈时间" prop="interviewTime">
            <el-date-picker
              v-model="formData.interviewTime"
              class="w-1/1"
              placeholder="请选择面谈时间"
              type="datetime"
              value-format="x"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="分类" prop="category">
            <el-select v-model="formData.category" class="w-1/1" placeholder="请选择分类">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_INTERVIEW_CATEGORY)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="所在地" prop="areaId">
            <el-cascader
              v-model="formData.areaId"
              :options="areaList"
              :props="defaultProps"
              class="w-1/1"
              clearable
              filterable
              placeholder="请选择所在地"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="客户名称" prop="customerId">
            <el-select
              v-model="formData.customerId"
              class="w-1/1"
              filterable
              placeholder="请选择客户"
            >
              <el-option
                v-for="item in customerList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="销售" prop="salesUserId">
            <el-select
              v-model="formData.salesUserId"
              class="w-1/1"
              clearable
              filterable
              placeholder="请选择销售"
            >
              <el-option
                v-for="item in userOptions"
                :key="item.id"
                :label="item.nickname"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="参与人员" prop="participantUserIds">
            <el-select
              v-model="formData.participantUserIds"
              class="w-1/1"
              clearable
              collapse-tags
              collapse-tags-tooltip
              filterable
              multiple
              placeholder="请选择参与人员"
            >
              <el-option
                v-for="item in userOptions"
                :key="item.id"
                :label="item.nickname"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="需要准备" prop="prepare">
            <el-input
              v-model="formData.prepare"
              :rows="3"
              placeholder="请输入需要准备的内容"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="备注" prop="remark">
            <el-input
              v-model="formData.remark"
              :rows="2"
              placeholder="请输入备注"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">提 交</el-button>
      <el-button @click="dialogVisible = false">结 束</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { defaultProps } from '@/utils/tree'
import { InterviewApi } from '@/api/crm/interview'
import * as CustomerApi from '@/api/crm/customer'
import * as UserApi from '@/api/system/user'
import * as AreaApi from '@/api/system/area'

defineOptions({ name: 'InterviewForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const customerList = ref<any[]>([]) // 客户下拉
const userOptions = ref<UserApi.UserVO[]>([]) // 用户下拉
const areaList = ref([]) // 地区列表

const formData = ref<any>({
  id: undefined,
  interviewTime: undefined,
  category: undefined,
  areaId: undefined,
  customerId: undefined,
  salesUserId: undefined,
  participantUserIds: [],
  prepare: undefined,
  remark: undefined
})
const formRules = reactive({
  interviewTime: [{ required: true, message: '面谈时间不能为空', trigger: 'change' }],
  category: [{ required: true, message: '分类不能为空', trigger: 'change' }],
  customerId: [{ required: true, message: '客户名称不能为空', trigger: 'change' }]
})
const formRef = ref() // 表单 Ref

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增客户面谈' : '编辑客户面谈'
  formType.value = type
  resetForm()
  // 加载下拉数据
  customerList.value = await CustomerApi.getCustomerSimpleList()
  userOptions.value = await UserApi.getSimpleUserList()
  areaList.value = await AreaApi.getAreaTree()
  // 修改时，加载数据
  if (id) {
    formLoading.value = true
    try {
      const data = await InterviewApi.getInterview(id)
      formData.value = {
        id: data.id,
        interviewTime: data.interviewTime ? new Date(data.interviewTime).getTime() : undefined,
        category: data.category,
        areaId: data.areaId,
        customerId: data.customerId,
        salesUserId: data.salesUserId,
        participantUserIds: data.participantUserIds || [],
        prepare: data.prepare,
        remark: data.remark
      }
    } finally {
      formLoading.value = false
    }
  } else {
    // 新增时，面谈时间默认当下
    formData.value.interviewTime = Date.now()
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  await formRef.value.validate()
  // 提交请求
  formLoading.value = true
  try {
    const data = { ...formData.value }
    if (formType.value === 'create') {
      await InterviewApi.createInterview(data)
      message.success(t('common.createSuccess'))
    } else {
      await InterviewApi.updateInterview(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    interviewTime: undefined,
    category: undefined,
    areaId: undefined,
    customerId: undefined,
    salesUserId: undefined,
    participantUserIds: [],
    prepare: undefined,
    remark: undefined
  }
  formRef.value?.resetFields()
}
</script>
